import type { CrewStatMixItem, CrewStatsCertRow, CrewStatsMedicalRow } from './types';
import type { ComplianceFocus } from './complianceFocus';

export type ComplianceStatsFocus = Exclude<
  ComplianceFocus,
  'intention-overdue' | 'nationalities' | 'coe-soon'
>;

export type NamedBucket = {
  key: string;
  label: string;
  bar: string;
  emphasize?: boolean;
};

export type ComplianceCountRow = {
  key: string;
  dim1: string;
  dim2: string;
  unknown: number;
  total: number;
  [bucketKey: string]: string | number;
};

export const OVERDUE_BUCKETS: NamedBucket[] = [
  { key: 'd1_30', label: '1–30d', bar: 'bg-amber-500' },
  { key: 'd31_90', label: '31–90d', bar: 'bg-orange-500' },
  { key: 'd91_180', label: '91–180d', bar: 'bg-rose-500' },
  { key: 'd181', label: '≥ 181d', bar: 'bg-rose-600', emphasize: true },
];

export const UNTIL_BUCKETS: NamedBucket[] = [
  { key: 'd0_30', label: '0–30d', bar: 'bg-rose-500', emphasize: true },
  { key: 'd31_60', label: '31–60d', bar: 'bg-amber-500' },
  { key: 'd61_90', label: '61–90d', bar: 'bg-indigo-500' },
  { key: 'd91', label: '≥ 91d', bar: 'bg-sky-500' },
];

function overdueKey(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return 'unknown';
  const overdue = days < 0 ? -days : 0;
  if (overdue <= 30) return 'd1_30';
  if (overdue <= 90) return 'd31_90';
  if (overdue <= 180) return 'd91_180';
  return 'd181';
}

function untilKey(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return 'unknown';
  if (days <= 30) return 'd0_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'd91';
}

export function buildDimCountRows<T>(
  items: T[],
  getDim1: (row: T) => string,
  getDim2: (row: T) => string,
  getBucket: (row: T) => string,
  buckets: NamedBucket[],
): ComplianceCountRow[] {
  const map = new Map<string, ComplianceCountRow>();
  for (const item of items) {
    const dim1 = getDim1(item).trim() || '—';
    const dim2 = getDim2(item).trim() || 'Unknown';
    const key = `${dim1}\0${dim2}`;
    let agg = map.get(key);
    if (!agg) {
      agg = { key, dim1, dim2, unknown: 0, total: 0 };
      for (const b of buckets) agg[b.key] = 0;
      map.set(key, agg);
    }
    const bucket = getBucket(item);
    if (bucket === 'unknown' || !buckets.some((b) => b.key === bucket)) {
      agg.unknown += 1;
    } else {
      agg[bucket] = Number(agg[bucket]) + 1;
    }
    agg.total += 1;
  }
  return Array.from(map.values()).sort((a, b) => {
    const by1 = a.dim1.localeCompare(b.dim1, undefined, { sensitivity: 'base' });
    if (by1 !== 0) return by1;
    return a.dim2.localeCompare(b.dim2, undefined, { sensitivity: 'base' });
  });
}

export function bucketMixFromCountRows(rows: ComplianceCountRow[], buckets: NamedBucket[]): CrewStatMixItem[] {
  const items: CrewStatMixItem[] = buckets.map((b) => ({
    label: b.label,
    count: rows.reduce((s, r) => s + Number(r[b.key] ?? 0), 0),
  }));
  const unknown = rows.reduce((s, r) => s + r.unknown, 0);
  if (unknown > 0) items.push({ label: 'Unknown', count: unknown });
  return items;
}

export function dim2MixFromCountRows(rows: ComplianceCountRow[]): CrewStatMixItem[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(row.dim2, (map.get(row.dim2) ?? 0) + row.total);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export type ComplianceCountSpec = {
  dim1Header: string;
  dim2Header: string;
  bucketGroup: string;
  chartTitle: string;
  chartHint: string;
  mixTitle: string;
  buckets: NamedBucket[];
  unknownLabel: string;
  metricLabel: string;
};

export const COMPLIANCE_COUNT_SPEC: Record<ComplianceStatsFocus, ComplianceCountSpec> = {
  'certs-expired': {
    dim1Header: 'Rank',
    dim2Header: 'Category',
    bucketGroup: 'Days overdue',
    chartTitle: 'Days overdue',
    chartHint: 'Past expiry',
    mixTitle: 'Category mix',
    buckets: OVERDUE_BUCKETS,
    unknownLabel: 'No date',
    metricLabel: 'Certificates',
  },
  'certs-expiring': {
    dim1Header: 'Rank',
    dim2Header: 'Category',
    bucketGroup: 'Days until expiry',
    chartTitle: 'Days until expiry',
    chartHint: 'Selected window',
    mixTitle: 'Category mix',
    buckets: UNTIL_BUCKETS,
    unknownLabel: 'No date',
    metricLabel: 'Certificates',
  },
  'medicals-expired': {
    dim1Header: 'Rank',
    dim2Header: 'Kind',
    bucketGroup: 'Days overdue',
    chartTitle: 'Days overdue',
    chartHint: 'Past next due',
    mixTitle: 'Kind mix',
    buckets: OVERDUE_BUCKETS,
    unknownLabel: 'No date',
    metricLabel: 'Medicals',
  },
  'medicals-due': {
    dim1Header: 'Rank',
    dim2Header: 'Kind',
    bucketGroup: 'Days until due',
    chartTitle: 'Days until due',
    chartHint: 'Selected window',
    mixTitle: 'Kind mix',
    buckets: UNTIL_BUCKETS,
    unknownLabel: 'No date',
    metricLabel: 'Medicals',
  },
};

export function buildComplianceCountRows(
  focus: ComplianceStatsFocus,
  input: {
    certs?: CrewStatsCertRow[];
    medicals?: CrewStatsMedicalRow[];
  },
): ComplianceCountRow[] {
  const spec = COMPLIANCE_COUNT_SPEC[focus];
  switch (focus) {
    case 'certs-expired':
    case 'certs-expiring':
      return buildDimCountRows(
        input.certs ?? [],
        (r) => r.rank || '—',
        (r) => r.category || 'Uncategorized',
        (r) => (focus === 'certs-expired' ? overdueKey(r.daysUntilExpiry) : untilKey(r.daysUntilExpiry)),
        spec.buckets,
      );
    case 'medicals-expired':
    case 'medicals-due':
      return buildDimCountRows(
        input.medicals ?? [],
        (r) => r.rank || '—',
        (r) => r.kind || 'Unknown',
        (r) => (focus === 'medicals-expired' ? overdueKey(r.daysUntilExpiry) : untilKey(r.daysUntilExpiry)),
        spec.buckets,
      );
  }
}
